/**
 * `Dentro de` (Figma `325:12257`, popover `638:18828`): the team a new team goes inside decides
 * its level, so this is the only place a level gets chosen -- there is no separate level select
 * to disagree with it. A team at the deepest level never appears: nothing can go inside it.
 */
import { useState, type ReactNode } from 'react';
import { Check, ChevronDown } from 'lucide-react';

import { Button } from '../../../ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../../ui/popover';
import type { LevelOut, TeamOut } from './schemas';
import { t } from '../i18n';

export interface ParentSelection {
  parentTeamId: string | null;
  levelId: string;
}

export interface ParentPickerProps {
  levels: LevelOut[];
  teams: TeamOut[];
  value: ParentSelection | null;
  onChange: (value: ParentSelection) => void;
  /** Omits "Sem equipe acima": `EditTeam` passes only the level directly above the team being
   *  edited and its own level, and that team's level never changes, so there is no "no team
   *  above" choice to offer. */
  excludeTop?: boolean;
}

interface ParentOption {
  key: string;
  team: TeamOut | null;
  selection: ParentSelection;
  label: string;
  path: string[];
}

interface ParentGroup {
  key: string;
  heading: string;
  options: ParentOption[];
}

function ancestorNames(team: TeamOut, byId: Map<string, TeamOut>): string[] {
  const names: string[] = [];
  let current = team.parent_team_id !== null ? byId.get(team.parent_team_id) : undefined;
  while (current !== undefined) {
    names.unshift(current.name);
    current = current.parent_team_id !== null ? byId.get(current.parent_team_id) : undefined;
  }
  return names;
}

function buildGroups(levels: LevelOut[], teams: TeamOut[], excludeTop: boolean): ParentGroup[] {
  const sortedLevels = [...levels].sort((a, b) => a.position - b.position);
  const first = sortedLevels[0];
  if (first === undefined) return [];
  const byId = new Map(teams.map((team) => [team.id, team]));

  const groups: ParentGroup[] = excludeTop
    ? []
    : [
        {
          key: 'top',
          heading: t('teams.newTeam.parent.topGroup', { level: first.name }),
          options: [
            {
              key: 'top',
              team: null,
              selection: { parentTeamId: null, levelId: first.id },
              label: t('teams.newTeam.parent.top'),
              path: [],
            },
          ],
        },
      ];

  for (let i = 0; i < sortedLevels.length - 1; i++) {
    const level = sortedLevels[i]!;
    const nextLevel = sortedLevels[i + 1]!;
    const levelTeams = teams.filter((team) => team.level_id === level.id);
    if (levelTeams.length === 0) continue;
    groups.push({
      key: level.id,
      heading: t('teams.newTeam.parent.group', { level: level.name, next: nextLevel.name }),
      options: levelTeams.map((team) => ({
        key: team.id,
        team,
        selection: { parentTeamId: team.id, levelId: nextLevel.id },
        label: team.name,
        path: ancestorNames(team, byId),
      })),
    });
  }

  return groups;
}

function pathOf(option: ParentOption): string {
  return [...option.path, option.label].join(' › ');
}

export function ParentPicker(props: ParentPickerProps): ReactNode {
  const { levels, teams, value, onChange, excludeTop = false } = props;
  const [open, setOpen] = useState(false);
  const groups = buildGroups(levels, teams, excludeTop);
  const allOptions = groups.flatMap((group) => group.options);
  const selectedOption =
    value === null
      ? undefined
      : allOptions.find(
          (option) =>
            option.selection.parentTeamId === value.parentTeamId &&
            option.selection.levelId === value.levelId,
        );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {/* tailwind-merge does not know this project's font-size tokens, so `Button`'s base
         *  `text-sm` is kept alongside `text-body-sm` and wins on stylesheet order (utilities
         *  are emitted alphabetically, and `text-sm` sorts after it). `!` is what lets the
         *  token's 1.5 line-height through. */}
        <Button
          type="button"
          variant="outline"
          aria-haspopup="listbox"
          aria-label={t('teams.newTeam.parent.label')}
          className="w-full justify-between border-input bg-card font-normal text-body-sm!"
        >
          <span
            className={selectedOption === undefined ? 'text-muted-foreground' : 'text-foreground'}
          >
            {selectedOption === undefined
              ? t('teams.newTeam.parent.placeholder')
              : pathOf(selectedOption)}
          </span>
          <ChevronDown aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0">
        <Command>
          <CommandInput placeholder={t('teams.newTeam.parent.searchPlaceholder')} />
          <CommandList>
            <CommandEmpty>{t('teams.newTeam.parent.empty')}</CommandEmpty>
            {groups.map((group) => (
              <CommandGroup
                key={group.key}
                heading={group.heading}
                className="[&_[cmdk-group-heading]]:uppercase"
              >
                {group.options.map((option) => {
                  const isSelected =
                    value !== null &&
                    value.parentTeamId === option.selection.parentTeamId &&
                    value.levelId === option.selection.levelId;
                  return (
                    // The heading is where "No topo" and the level created live, so a search
                    // for either must still find the options under it.
                    <CommandItem
                      key={option.key}
                      value={pathOf(option)}
                      keywords={[group.heading]}
                      onSelect={() => {
                        onChange(option.selection);
                        setOpen(false);
                      }}
                    >
                      <div className="flex flex-1 flex-col">
                        <span>{option.label}</span>
                        {option.path.length > 0 && (
                          <span className="text-caption text-muted-foreground">
                            {option.path.join(' › ')}
                          </span>
                        )}
                      </div>
                      {isSelected && <Check aria-hidden className="size-3.5" />}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
